import React, { useMemo, useState } from 'react';
import { RotateCcwIcon } from 'lucide-react';
import { LoanRecord } from '../../types/highlights';
import { useHighlightsPanel } from '../../hooks/useHighlightsPanel';
import { fieldCatalog } from '../../data/fieldCatalog';
import { textScaleStyles } from '../../utils/layout';
import { evaluateField } from '../../utils/rules';
import { HighlightChip } from './HighlightChip';
import { AddFieldControl } from './AddFieldControl';
import { HighlightSettings } from './HighlightSettings';
import { RulesEditor } from './RulesEditor';
interface HighlightsPanelProps {
  loan: LoanRecord;
  rulesEnabled: boolean;
}
export function HighlightsPanel({
  loan,
  rulesEnabled
}: HighlightsPanelProps) {
  const {
    fields,
    byId,
    availableFields,
    toneColors,
    rules,
    layout,
    addField,
    removeField,
    setColorOverride,
    clearAllOverrides,
    moveField,
    setFieldSpan,
    setFieldMaxChars,
    setLayout,
    normalizeFieldSizes,
    setToneColor,
    addRule,
    updateRule,
    removeRule,
    resetFields,
    resetHighlightSettings
  } = useHighlightsPanel();
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rulesFocusFieldId, setRulesFocusFieldId] = useState<string | null>(null);
  const activeRules = rulesEnabled ? rules : [];
  const matches = useMemo(() => {
    const map = new Map<string, ReturnType<typeof evaluateField>>();
    fields.forEach(field => {
      map.set(field.fieldId, evaluateField(field.fieldId, activeRules, loan, byId));
    });
    return map;
  }, [fields, activeRules, loan, byId]);
  const attentionCount = useMemo(() => fields.filter(field => {
    if (field.colorOverride === 'critical' || field.colorOverride === 'warning') return true;
    if (field.colorOverride) return false;
    const match = matches.get(field.fieldId);
    return match?.tone === 'critical' || match?.tone === 'warning';
  }).length, [fields, matches]);
  const openRules = (fieldId: string | null) => {
    setRulesFocusFieldId(fieldId);
    setRulesOpen(true);
  };
  const handleDrop = (index: number) => {
    if (dragFrom !== null) moveField(dragFrom, index);
    setDragFrom(null);
    setDragOver(null);
  };
  return <section aria-label="Loan highlights" className="w-full rounded-md border border-[#d6e0f0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.06)]" data-id="element-100" data-mp="3e4o62hnrwmbu">
      <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-[#e6ecf7] px-4 py-2.5" data-id="element-101" data-mp="2mgnpl0xwwyaw">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1" data-id="element-102" data-mp="2qfwjq6wv6y6z">
          <h2 className="text-sm font-bold text-slate-900" data-id="element-103" data-mp="2v8xzsi8t53s5">Highlights</h2>
          <span className="text-[12px] text-slate-500" data-id="element-104" data-mp="1yqjnwz4ab8mj">
            {fields.length} {fields.length === 1 ? 'field' : 'fields'}
          </span>
          {attentionCount > 0 ? <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800" data-id="element-105" data-mp="1v840vutkogo9">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-amber-600" data-id="element-106" data-mp="1q9sr1k3a2661" />
              {attentionCount} need{attentionCount === 1 ? 's' : ''} attention
            </span> : <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800" data-id="element-107" data-mp="1h81kkd0wxvb4">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-emerald-600" data-id="element-108" data-mp="17hq8u4azmhto" />
              No flags
            </span>}
        </div>

        <div className="flex items-center gap-2" data-id="element-109" data-mp="yi3icdjsqwfe">
          <AddFieldControl availableFields={availableFields} loan={loan} variant="button" onAdd={addField} data-id="element-110" data-mp="yjelqc9by25z" />
          <HighlightSettings fields={fields} byId={byId} loan={loan} toneColors={toneColors} rules={rules} layout={layout} onSetToneColor={setToneColor} onClearAllOverrides={clearAllOverrides} onSetLayout={setLayout} onNormalizeFieldSizes={normalizeFieldSizes} onManageRules={openRules} onResetSettings={resetHighlightSettings} data-id="element-111" data-mp="ojdyif8cb1wi" />
          <button type="button" onClick={resetFields} title="Reset layout" className="flex items-center gap-1.5 rounded-md border border-transparent px-2 py-1.5 text-[12px] font-medium text-slate-500 transition hover:bg-slate-50 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab]" data-id="element-112" data-mp="25uw31ceery6c">
            <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-113" data-mp="3qb94ezkywzkk" />
            Reset
          </button>
        </div>
      </header>

      <div className="grid items-center gap-2 px-4 pb-4 pt-3.5" style={{
      gridTemplateColumns: `repeat(auto-fill, minmax(${layout.columnWidth}px, 1fr))`
    }} data-id="element-114" data-mp="3valo5bgjlqix">
        {fields.map((field, index) => {
        const definition = byId.get(field.fieldId);
        if (!definition) return null;
        return <HighlightChip key={field.fieldId} definition={definition} field={field} loan={loan} index={index} total={fields.length} toneColors={toneColors} layout={layout} match={matches.get(field.fieldId) ?? null} ruleCount={rules.filter(rule => rule.targetFieldId === field.fieldId).length} isDragTarget={dragOver === index && dragFrom !== null && dragFrom !== index} onDragStartChip={setDragFrom} onDragOverChip={setDragOver} onDropChip={handleDrop} onDragEndChip={() => {
          setDragFrom(null);
          setDragOver(null);
        }} onMove={moveField} onRemove={removeField} onSetColorOverride={setColorOverride} onSetSpan={setFieldSpan} onSetMaxChars={setFieldMaxChars} onManageRules={openRules} data-id="element-115" data-mp="1temodagvn0r8" data-mpi={index} />;
      })}

        <AddFieldControl availableFields={availableFields} loan={loan} variant="chip" heightClass={textScaleStyles[layout.textScale].height} onAdd={addField} data-id="element-116" data-mp="1gn9pco2lrrwi" />
      </div>

      <RulesEditor open={rulesOpen} focusFieldId={rulesFocusFieldId} fields={fields} catalog={fieldCatalog} byId={byId} rules={rules} loan={loan} toneColors={toneColors} onAddRule={addRule} onUpdateRule={updateRule} onRemoveRule={removeRule} onClose={() => setRulesOpen(false)} data-id="element-117" data-mp="1pl25fug5f3ky" />
    </section>;
}